/**
 * Audio Stream Player
 *
 * Web Audio API-based audio player for streaming playback of audio chunks.
 * Designed for real-time TTS where audio arrives in small chunks that need
 * to be played seamlessly without gaps.
 *
 * Key features:
 * - Gapless playback of audio chunks
 * - Pre-buffering to prevent underruns
 * - PCM and MP3 format support
 * - Immediate interruption support
 */

export type AudioPlayerState = 'idle' | 'buffering' | 'playing' | 'paused' | 'stopped'

export interface AudioPlayerConfig {
  sampleRate: number
  channels?: number
  preBufferMs?: number // Buffer before starting playback (default: 100ms)
}

export interface AudioPlayerEvents {
  onStateChange: (state: AudioPlayerState) => void
  onError: (error: Error) => void
  onPlaybackComplete: () => void
}

interface QueuedBuffer {
  buffer: AudioBuffer
  scheduledTime: number
}

export class AudioStreamPlayer {
  private audioContext: AudioContext | null = null
  private queue: QueuedBuffer[] = []
  private state: AudioPlayerState = 'idle'
  private nextPlayTime = 0
  private isStreamComplete = false
  // The stream's end has been reported (once per stream)
  private ended = false
  // MP3 decodes in flight for this stream; every end waits for them
  private pendingDecodes = 0
  // Bumped by interrupt(): a decode from an earlier stream is dropped
  private generation = 0
  private preBufferMs: number
  private preBufferTimer: ReturnType<typeof setTimeout> | null = null
  private accumulatedDuration = 0
  private sampleRate: number
  private channels: number
  private events: AudioPlayerEvents

  // Track currently playing sources for interruption
  private activeSources: AudioBufferSourceNode[] = []

  constructor(config: AudioPlayerConfig, events: AudioPlayerEvents) {
    this.sampleRate = config.sampleRate
    this.channels = config.channels ?? 1
    this.preBufferMs = config.preBufferMs ?? 100
    this.events = events
  }

  /**
   * Get current playback state
   */
  getState(): AudioPlayerState {
    return this.state
  }

  /**
   * Get the underlying AudioContext state.
   * Useful for external verification that audio will actually play.
   */
  getAudioContextState(): AudioContextState | null {
    return this.audioContext?.state ?? null
  }

  /**
   * Initialize or resume AudioContext (must be called after user interaction)
   */
  async initialize(): Promise<void> {
    if (!this.audioContext) {
      this.audioContext = new AudioContext({ sampleRate: this.sampleRate })
    }

    if (this.audioContext.state === 'suspended') {
      try {
        await this.audioContext.resume()
      } catch (error) {
        console.error('[AudioStreamPlayer] Failed to resume AudioContext:', error)
        throw error
      }
    }
  }


  /**
   * Enqueue a PCM audio chunk for playback
   *
   * @param pcmData - Raw PCM audio data (Int16 or Float32)
   */
  enqueuePCM(pcmData: ArrayBuffer): void {
    if (!this.audioContext) {
      console.warn('[AudioStreamPlayer] Not initialized - no AudioContext')
      return
    }

    // Try to resume if suspended (best effort, may not work without user gesture)
    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume().catch(() => {})
    }

    try {
      const audioBuffer = this.pcmToAudioBuffer(pcmData)
      this.enqueueBuffer(audioBuffer)
    } catch (error) {
      console.error('[AudioStreamPlayer] Failed to process PCM:', error)
      this.events.onError(
        error instanceof Error ? error : new Error('Failed to process PCM')
      )
    }
  }

  /**
   * Enqueue an MP3 audio chunk for playback
   *
   * @param mp3Data - MP3 audio data
   */
  async enqueueMP3(mp3Data: ArrayBuffer): Promise<void> {
    if (!this.audioContext) {
      console.warn('[AudioStreamPlayer] Not initialized')
      return
    }

    const gen = this.generation
    this.pendingDecodes++
    try {
      // MP3 needs async decoding
      const audioBuffer = await this.audioContext.decodeAudioData(mp3Data.slice(0))
      if (gen !== this.generation) return
      this.enqueueBuffer(audioBuffer)
    } catch (error) {
      console.error('[AudioStreamPlayer] Failed to decode MP3:', error)
      // MP3 chunks might be incomplete, don't treat as fatal
    } finally {
      if (gen === this.generation) {
        this.pendingDecodes--
        this.endIfOver()
      }
    }
  }

  /**
   * Mark the stream as complete (no more chunks coming)
   */
  markComplete(): void {
    if (this.isStreamComplete) return
    this.isStreamComplete = true

    // If we're still buffering, start playback immediately
    if (this.state === 'buffering' && this.queue.length > 0) {
      this.startPlayback()
    }

    this.endIfOver()
  }

  /**
   * Report the stream's end, once, when it is complete, no decode is in
   * flight and nothing is left to play. An idle player with nothing queued
   * reports it without a state change: no audio ever played (SA-1402).
   * Everything scheduled having already played leaves no `onended` to end
   * the playback, so it ends here too (SA-1392).
   */
  private endIfOver(): void {
    if (!this.isStreamComplete || this.pendingDecodes > 0) return
    if (this.queue.length !== 0) return

    if (this.state === 'playing' && this.activeSources.length === 0) {
      this.setState('idle')
    } else if (this.state !== 'idle') {
      return
    }
    // The latch gates only the report: a buffer that joins a stream after
    // it ended still plays and still returns the player to idle.
    if (this.ended) return
    this.ended = true
    this.events.onPlaybackComplete()
  }

  /**
   * Immediately stop playback and clear all queued audio
   */
  interrupt(): void {
    // Stop all active sources
    for (const source of this.activeSources) {
      try {
        source.stop()
        source.disconnect()
      } catch {
        // Ignore errors from already-stopped sources
      }
    }
    this.activeSources = []

    // Clear queue
    this.queue = []
    this.accumulatedDuration = 0
    this.isStreamComplete = false
    this.ended = false
    this.pendingDecodes = 0
    this.generation++

    // Clear pre-buffer timer
    if (this.preBufferTimer) {
      clearTimeout(this.preBufferTimer)
      this.preBufferTimer = null
    }

    // Reset timing
    this.nextPlayTime = 0

    this.setState('stopped')
  }

  /**
   * Pause playback (can be resumed)
   */
  async pause(): Promise<void> {
    if (this.audioContext && this.state === 'playing') {
      await this.audioContext.suspend()
      this.setState('paused')
    }
  }

  /**
   * Resume paused playback
   */
  async resume(): Promise<void> {
    if (this.audioContext && this.state === 'paused') {
      await this.audioContext.resume()
      this.setState('playing')
    }
  }

  /**
   * Clean up resources
   */
  async destroy(): Promise<void> {
    this.interrupt()

    if (this.audioContext) {
      await this.audioContext.close()
      this.audioContext = null
    }

    this.setState('idle')
  }

  /**
   * Reset player for new stream
   */
  reset(): void {
    this.interrupt()
    this.isStreamComplete = false
    this.setState('idle')
  }

  /**
   * Convert Int16 PCM data to AudioBuffer
   */
  private pcmToAudioBuffer(pcmData: ArrayBuffer): AudioBuffer {
    if (!this.audioContext) {
      throw new Error('AudioContext not initialized')
    }

    // PCM from ElevenLabs is Int16
    const int16Array = new Int16Array(pcmData)
    const float32Array = new Float32Array(int16Array.length)

    // Convert Int16 (-32768 to 32767) to Float32 (-1.0 to 1.0)
    for (let i = 0; i < int16Array.length; i++) {
      float32Array[i] = int16Array[i] / 32768
    }

    // Create AudioBuffer
    const audioBuffer = this.audioContext.createBuffer(
      this.channels,
      float32Array.length,
      this.sampleRate
    )

    // Copy data to buffer
    audioBuffer.copyToChannel(float32Array, 0)

    // If stereo, copy to second channel too (mono source)
    if (this.channels > 1) {
      audioBuffer.copyToChannel(float32Array, 1)
    }

    return audioBuffer
  }

  /**
   * Enqueue an AudioBuffer for playback
   */
  private enqueueBuffer(buffer: AudioBuffer): void {
    this.accumulatedDuration += buffer.duration * 1000 // Convert to ms

    // Queue the buffer (will schedule later)
    this.queue.push({ buffer, scheduledTime: 0 })

    // Handle state transitions
    if (this.state === 'idle') {
      this.setState('buffering')

      // Check if we already have enough audio (first chunk might be large enough)
      if (this.accumulatedDuration >= this.preBufferMs) {
        this.startPlayback()
      } else {
        // Start pre-buffer timer
        this.preBufferTimer = setTimeout(() => {
          if (this.queue.length > 0 && this.state === 'buffering') {
            this.startPlayback()
          }
        }, this.preBufferMs)
      }
    } else if (this.state === 'buffering') {
      // Check if we've accumulated enough audio
      if (this.accumulatedDuration >= this.preBufferMs) {
        if (this.preBufferTimer) {
          clearTimeout(this.preBufferTimer)
          this.preBufferTimer = null
        }
        this.startPlayback()
      }
    } else if (this.state === 'playing') {
      // Schedule immediately if already playing
      this.scheduleBuffer(this.queue[this.queue.length - 1])
    }
  }

  /**
   * Start playback of queued buffers
   */
  private startPlayback(): void {
    if (!this.audioContext || this.queue.length === 0) return

    // If suspended, try to resume (fire-and-forget, schedule playback anyway)
    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume().catch(() => {})
    }

    this.setState('playing')

    // Initialize next play time
    this.nextPlayTime = this.audioContext.currentTime

    // Schedule all queued buffers
    for (const queuedBuffer of this.queue) {
      this.scheduleBuffer(queuedBuffer)
    }
  }

  /**
   * Schedule a single buffer for playback
   */
  private scheduleBuffer(queuedBuffer: QueuedBuffer): void {
    if (!this.audioContext) {
      return
    }

    const source = this.audioContext.createBufferSource()
    source.buffer = queuedBuffer.buffer
    source.connect(this.audioContext.destination)

    // Track for interruption
    this.activeSources.push(source)

    // Calculate start time
    const startTime = Math.max(this.audioContext.currentTime, this.nextPlayTime)
    queuedBuffer.scheduledTime = startTime

    // Update next play time for seamless playback
    this.nextPlayTime = startTime + queuedBuffer.buffer.duration

    // Clean up when done
    source.onended = () => {
      const index = this.activeSources.indexOf(source)
      if (index !== -1) {
        this.activeSources.splice(index, 1)
      }

      // Remove from queue
      const queueIndex = this.queue.indexOf(queuedBuffer)
      if (queueIndex !== -1) {
        this.queue.splice(queueIndex, 1)
      }

      // Check if playback is complete
      this.endIfOver()
    }

    // Start playback
    source.start(startTime)
  }

  /**
   * Update state and notify listeners
   */
  private setState(newState: AudioPlayerState): void {
    if (this.state !== newState) {
      this.state = newState
      this.events.onStateChange(newState)
    }
  }
}

/**
 * Create an AudioStreamPlayer with default configuration
 */
export function createAudioStreamPlayer(
  sampleRate: number,
  events: AudioPlayerEvents,
  preBufferMs?: number
): AudioStreamPlayer {
  return new AudioStreamPlayer(
    {
      sampleRate,
      channels: 1,
      preBufferMs,
    },
    events
  )
}
